import type { Relation, RelationType } from '../types/relations';
import type { FourPillars, PillarPosition } from '../types/pillars';
import { branchIndex } from '../core/sexagenary';

/**
 * 동력선후 우선순위 tier 맵 (1=최고, 8=최저)
 *
 * 교재 기준 (정암명리 형충회합 종합):
 * 1선: 간합/천간충  2선: 방합/삼합/삼형  3선: 육충/육합
 * 4선: 상형/자묘형/자형  5선: 반합  6선: 파/해  7선: 원진  8선: 사고합
 */
const PRIORITY_TIERS: Record<RelationType, number> = {
  '천간합': 1,
  '천간충': 1,
  '방합': 2,
  '삼합': 2,
  '삼형': 2,
  '육충': 3,
  '육합': 3,
  '상형': 4,
  '자묘형': 4,
  '자형': 4,
  '반합': 5,
  '파': 6,
  '해': 6,
  '원진': 7,
  '사고합': 8,
};

/** 천간 관계인지 여부 */
function isHeavenlyRelation(r: Relation): boolean {
  return r.type === '천간합' || r.type === '천간충';
}

/** 기둥 순서 (시제의 법칙용) */
const PILLAR_ORDER: Record<PillarPosition, number> = {
  year: 0, month: 1, day: 2, hour: 3,
};

/**
 * 우선순위 해소 (동력선후)
 *
 * 같은 pillar position을 두고 여러 관계가 경쟁할 때,
 * 높은 tier의 관계가 낮은 tier를 suppressed 처리한다.
 * 동일 tier 충돌 시 타이브레이커 적용.
 *
 * 천간(stems)과 지지(branches) 도메인은 독립 처리.
 */
export function resolveRelationPriority(
  relations: Relation[],
  fourPillars: FourPillars,
  noHour?: boolean,
): void {
  const positions: readonly PillarPosition[] = noHour
    ? ['year', 'month', 'day']
    : ['year', 'month', 'day', 'hour'];

  // 1. 모든 관계에 priority tier 설정
  for (const r of relations) {
    r.priority = PRIORITY_TIERS[r.type] ?? 8;
  }

  // 2. 천간/지지 도메인 분리
  const heavenly = relations.filter(isHeavenlyRelation);
  const earthly = relations.filter(r => !isHeavenlyRelation(r));

  // 3. 각 도메인별 해소
  resolveWithinDomain(heavenly, fourPillars, 'stem', positions);
  resolveWithinDomain(earthly, fourPillars, 'branch', positions);
}

function resolveWithinDomain(
  relations: Relation[],
  fourPillars: FourPillars,
  domain: 'stem' | 'branch',
  positions: readonly PillarPosition[],
): void {
  if (relations.length <= 1) return;

  // 각 position별 최소(최고) tier 계산
  const minTierByPos = new Map<PillarPosition, number>();
  for (const r of relations) {
    if (r.suppressed) continue;
    const tier = r.priority ?? 8;
    for (const p of r.pillars) {
      const current = minTierByPos.get(p) ?? Infinity;
      minTierByPos.set(p, Math.min(current, tier));
    }
  }

  // 관계의 모든 참여 position에서 해당 tier보다 높은 우선순위가 있으면 suppress
  for (const r of relations) {
    if (r.suppressed) continue;
    const tier = r.priority ?? 8;

    for (const p of r.pillars) {
      const minTier = minTierByPos.get(p) ?? Infinity;
      if (tier > minTier) {
        r.suppressed = true;
        break;
      }
    }
  }

  // 동일 tier 충돌 해소 (타이브레이커)
  resolveSameTierConflicts(relations, fourPillars, domain, positions);
}

/**
 * 동일 tier에서 같은 position을 두고 경쟁하는 관계를 해소
 *
 * 지지 → 강약의 법칙 (원국에서 더 많이 등장하는 지지 우선)
 * 천간 → 시제의 법칙 (더 앞 기둥 우선)
 */
function resolveSameTierConflicts(
  relations: Relation[],
  fourPillars: FourPillars,
  domain: 'stem' | 'branch',
  positions: readonly PillarPosition[],
): void {
  const active = relations.filter(r => !r.suppressed);

  // position별로 같은 tier의 관계들을 그룹핑
  const posRelations = new Map<PillarPosition, Relation[]>();
  for (const r of active) {
    for (const p of r.pillars) {
      const list = posRelations.get(p) ?? [];
      list.push(r);
      posRelations.set(p, list);
    }
  }

  for (const [, rels] of posRelations) {
    if (rels.length <= 1) continue;

    // 같은 tier끼리 그룹
    const tierGroups = new Map<number, Relation[]>();
    for (const r of rels) {
      const tier = r.priority ?? 8;
      const group = tierGroups.get(tier) ?? [];
      group.push(r);
      tierGroups.set(tier, group);
    }

    for (const [, group] of tierGroups) {
      if (group.length <= 1) continue;

      if (domain === 'branch') {
        // 강약의 법칙: 원국에서 해당 지지가 더 많이 등장하는 관계가 우선
        resolveByStrength(group, fourPillars, positions);
      } else {
        // 시제의 법칙: 더 앞 기둥(년>월>시)에 위치한 관계가 우선
        resolveByTemporal(group);
      }
    }
  }
}

/** 강약의 법칙: 관여 지지의 원국 등장 횟수로 판단 */
function resolveByStrength(conflicting: Relation[], fourPillars: FourPillars, positions: readonly PillarPosition[]): void {
  // 원국 지지 등장 횟수 카운트 (noHour 대응: positions만 사용)
  const branchCount = new Map<number, number>();
  for (const pos of positions) {
    const idx = branchIndex(fourPillars[pos].branch);
    branchCount.set(idx, (branchCount.get(idx) || 0) + 1);
  }

  // 각 관계의 "강도" = 관여 지지들의 총 등장 횟수
  const strengths = conflicting.map(r => {
    const score = (r.branches ?? []).reduce((sum, b) => sum + (branchCount.get(branchIndex(b)) || 0), 0);
    return { relation: r, score };
  });

  const maxScore = Math.max(...strengths.map(s => s.score));

  // 최강이 아닌 관계를 suppress
  for (const { relation, score } of strengths) {
    if (score < maxScore) {
      relation.suppressed = true;
    }
  }
}

/** 시제의 법칙: 더 앞 기둥이 우선 (년=0 < 월=1 < 일=2 < 시=3) */
function resolveByTemporal(conflicting: Relation[]): void {
  // 각 관계의 "시제 점수" = 참여 position 중 가장 앞 기둥의 순서
  const temporals = conflicting.map(r => {
    const minOrder = Math.min(...r.pillars.map(p => PILLAR_ORDER[p]));
    return { relation: r, minOrder };
  });

  const bestOrder = Math.min(...temporals.map(t => t.minOrder));

  // 가장 앞이 아닌 관계를 suppress
  for (const { relation, minOrder } of temporals) {
    if (minOrder > bestOrder) {
      relation.suppressed = true;
    }
  }
}
